import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Edit, ArrowLeft, Star, Bed, Bath, Car, Maximize2, MapPin } from 'lucide-react';
import api from '../../services/api';
import PortaisPanel from './PortaisPanel';
import Badge from '../../components/shared/Badge';

const TIPO_LABELS = {
  apartamento: 'Apartamento', cobertura: 'Cobertura', studio: 'Studio',
  duplex: 'Duplex', triplex: 'Triplex', casa: 'Casa',
  casa_condominio: 'Casa em Condomínio', sobrado: 'Sobrado',
  terreno: 'Terreno', lote: 'Lote', sala_comercial: 'Sala Comercial',
  loja: 'Loja', galpao: 'Galpão', flat: 'Flat', kitnet: 'Kitnet',
};

function formatarPreco(v) {
  if (!v) return null;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(v);
}

export default function ImovelDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fotoIdx, setFotoIdx] = useState(0);
  const [aba, setAba] = useState('dados');

  const { data, isLoading } = useQuery({
    queryKey: ['imovel', id],
    queryFn: () => api.get(`/imoveis/${id}`).then(r => r.data.data),
  });

  if (isLoading) return <div className="p-8 text-gray-400 text-center">Carregando...</div>;
  if (!data) return <div className="p-8 text-gray-400 text-center">Imóvel não encontrado</div>;

  const fotos = data.imoveis_fotos || [];
  const fotoAtual = fotos[fotoIdx];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={() => navigate('/imoveis')} className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-700 mb-2">
            <ArrowLeft size={16} /> Voltar
          </button>
          <h1 className="text-2xl font-bold text-gray-800">{data.titulo}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {data.codigo_interno} · {TIPO_LABELS[data.tipo_imovel]} · {data.empreendimento_nome}
          </p>
        </div>
        <button
          onClick={() => navigate(`/imoveis/${id}/editar`)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary"
        >
          <Edit size={16} /> Editar
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Galeria */}
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-gray-100 rounded-xl overflow-hidden aspect-video flex items-center justify-center">
            {fotoAtual ? (
              <img src={fotoAtual.url} alt="" className="w-full h-full object-cover" />
            ) : (
              <p className="text-gray-400 text-sm">Nenhuma foto cadastrada</p>
            )}
          </div>
          {fotos.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {fotos.map((f, i) => (
                <button
                  key={f.id}
                  onClick={() => setFotoIdx(i)}
                  className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 ${i === fotoIdx ? 'border-secondary' : 'border-transparent'}`}
                >
                  <img src={f.url_thumbnail || f.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Abas */}
          <div className="border-b border-gray-200 flex gap-6 mt-4">
            {[['dados', 'Dados'], ['portais', 'Portais']].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setAba(v)}
                className={`pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${aba === v ? 'border-secondary text-secondary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                {l}
              </button>
            ))}
          </div>

          {aba === 'dados' && (
            <div className="space-y-4">
              {/* Características */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { icon: Bed, label: 'Dormitórios', value: data.num_dormitorios },
                  { icon: Star, label: 'Suítes', value: data.num_suites },
                  { icon: Bath, label: 'Banheiros', value: data.num_banheiros },
                  { icon: Car, label: 'Vagas', value: data.num_vagas },
                  { icon: Maximize2, label: 'Área priv.', value: data.area_privativa ? `${data.area_privativa}m²` : null },
                  { icon: Maximize2, label: 'Área total', value: data.area_total ? `${data.area_total}m²` : null },
                ].filter(i => i.value).map(({ icon: Icon, label, value }) => (
                  <div key={label} className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
                    <Icon size={18} className="text-secondary" />
                    <div>
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className="font-semibold text-gray-800 text-sm">{value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Descrição */}
              {data.descricao && (
                <div>
                  <h3 className="font-medium text-gray-700 mb-2">Descrição</h3>
                  <p className="text-sm text-gray-600 whitespace-pre-line">{data.descricao}</p>
                </div>
              )}

              {/* Diferenciais */}
              {data.diferenciais?.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-700 mb-2">Diferenciais</h3>
                  <div className="flex flex-wrap gap-2">
                    {data.diferenciais.map(d => (
                      <span key={d} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">{d}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {aba === 'portais' && <PortaisPanel imovelId={id} />}
        </div>

        {/* Sidebar direita */}
        <div className="space-y-4">
          {/* Preço */}
          <div className="bg-white rounded-xl shadow p-4">
            <div className="space-y-2">
              {data.preco_venda && (
                <div>
                  <p className="text-xs text-gray-500">Preço de Venda</p>
                  <p className="text-2xl font-bold text-gray-800">{formatarPreco(data.preco_venda)}</p>
                </div>
              )}
              {data.preco_locacao && (
                <div>
                  <p className="text-xs text-gray-500">Locação</p>
                  <p className="text-lg font-semibold text-gray-700">{formatarPreco(data.preco_locacao)}<span className="text-sm font-normal text-gray-400">/mês</span></p>
                </div>
              )}
              {data.condominio && <p className="text-xs text-gray-500">Condomínio: {formatarPreco(data.condominio)}/mês</p>}
              {data.iptu && <p className="text-xs text-gray-500">IPTU: {formatarPreco(data.iptu)}/ano</p>}
            </div>
            <div className="flex gap-2 mt-3">
              {data.aceita_financiamento && <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">Financiamento</span>}
              {data.aceita_fgts && <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">FGTS</span>}
            </div>
          </div>

          {/* Endereço */}
          {(data.bairro || data.cidade) && (
            <div className="bg-white rounded-xl shadow p-4">
              <div className="flex items-start gap-2">
                <MapPin size={16} className="text-secondary mt-0.5" />
                <div>
                  <p className="text-sm text-gray-700">{[data.endereco, data.numero].filter(Boolean).join(', ')}</p>
                  {data.complemento && <p className="text-xs text-gray-500">{data.complemento}</p>}
                  <p className="text-sm text-gray-600">{data.bairro} — {data.cidade}/{data.estado}</p>
                  {data.andar && <p className="text-xs text-gray-500">{data.andar}º andar{data.bloco ? `, Bloco ${data.bloco}` : ''}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Status */}
          <div className="bg-white rounded-xl shadow p-4 space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Status</span>
              <Badge label={data.status_anuncio} variant={data.status_anuncio} />
            </div>
            {data.destaque && <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Destaque</span>
              <span className="text-yellow-600 font-medium">⭐ Sim</span>
            </div>}
            {data.responsavel_nome && <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Responsável</span>
              <span className="text-gray-800">{data.responsavel_nome}</span>
            </div>}
          </div>
        </div>
      </div>
    </div>
  );
}
